import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Typography } from '@mui/joy';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../main/context/AuthContext';
import { checkProfileEligibility } from '../../main/API/customer';
import {
  isCustomerPendingApproval,
  isPendingApproval403Detail,
  userHasCustomerRole,
} from '../../main/utils/customerAccountApproval';
import CustomerAppBar from './CustomerAppBar';
import CustomerPendingApprovalScreen from './CustomerPendingApprovalScreen';

/** Shared in-flight/completed verify sync so layout remounts (e.g. StrictMode) do not repeat the call. */
let customerUserVerifyPromise = null;

/**
 * CustomerLayout wraps all customer routes: app bar + content, and gates the portal when the
 * account is inactive (pending admin approval) unless profile completion is still required.
 *
 * Intended order for new inactive customers:
 * 1) Complete profile (eligible === true)
 * 2) Wait for admin approval (eligible === false)
 */
export default function CustomerLayout({ children }) {
  const { t } = useTranslation('ui');
  const { user, userRoles, loading: authLoading, refreshUserFromVerify } = useAuth();
  const location = useLocation();
  const pending = isCustomerPendingApproval(user, userRoles);

  const [userSynced, setUserSynced] = useState(false);
  const [gate, setGate] = useState({ loading: false, eligible: null, checkFailed: false });
  const [eligibilityRetryKey, setEligibilityRetryKey] = useState(0);

  // Sync is_active from /auth/verify/ so pending approval is detected without a manual refresh.
  useEffect(() => {
    if (authLoading) {
      setUserSynced(false);
      return;
    }

    let cancelled = false;
    (async () => {
      let storedUser = null;
      let storedRoles = [];
      try {
        const rawUser = localStorage.getItem('user_data');
        if (rawUser) storedUser = JSON.parse(rawUser);
      } catch (e) {
        storedUser = null;
      }
      try {
        const rawRoles = localStorage.getItem('user_roles');
        if (rawRoles) storedRoles = JSON.parse(rawRoles);
      } catch (e) {
        storedRoles = [];
      }

      if (userHasCustomerRole(storedRoles, storedUser)) {
        try {
          if (!customerUserVerifyPromise) {
            customerUserVerifyPromise = refreshUserFromVerify();
          }
          await customerUserVerifyPromise;
        } catch (e) {
          customerUserVerifyPromise = null;
          console.warn('CustomerLayout: refreshUserFromVerify failed', e);
        }
      }
      if (!cancelled) setUserSynced(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [authLoading, refreshUserFromVerify]);

  useEffect(() => {
    if (!pending) {
      setGate({ loading: false, eligible: null, checkFailed: false });
      return;
    }

    let cancelled = false;
    (async () => {
      setGate((g) => ({ ...g, loading: true, checkFailed: false }));
      try {
        const response = await checkProfileEligibility();
        if (cancelled) return;

        if (response && response.status === 200) {
          const eligible = !!(response.data && response.data.eligible);
          // eligible true → complete profile first; false → wait for admin approval
          setGate({ loading: false, eligible, checkFailed: false });
        } else {
          // Non-200 without throw: treat as not eligible (wait), not a transient failure
          setGate({ loading: false, eligible: false, checkFailed: false });
        }
      } catch (err) {
        if (cancelled) return;
        const status = err && err.response && err.response.status;
        const detail = err && err.response && err.response.data && err.response.data.detail;
        // Explicit pending-approval 403 → wait screen. Other errors → retry UI (do not
        // pretend the user is waiting for approval when eligibility could not be checked).
        if (status === 403 && isPendingApproval403Detail(detail)) {
          setGate({ loading: false, eligible: false, checkFailed: false });
        } else {
          setGate({ loading: false, eligible: null, checkFailed: true });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pending, user, eligibilityRetryKey]);

  if (authLoading || !userSynced) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#fafafa',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body1" color="neutral">
          {t('protected.loading')}
        </Typography>
      </Box>
    );
  }

  if (!pending) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
        <CustomerAppBar />
        <Box sx={{ pt: '64px' }}>{children}</Box>
      </Box>
    );
  }

  if (gate.checkFailed) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#fafafa',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          px: 2,
        }}
      >
        <Typography level="body1" color="neutral" sx={{ textAlign: 'center', maxWidth: 360 }}>
          {t('customer.profileEligibilityCheckFailed')}
        </Typography>
        <Button
          variant="solid"
          color="primary"
          onClick={() => setEligibilityRetryKey((k) => k + 1)}
        >
          {t('actions.retry')}
        </Button>
      </Box>
    );
  }

  if (gate.loading || gate.eligible === null) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#fafafa',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <CircularProgress size="lg" />
        <Typography level="body1" color="neutral">
          {t('protected.loading')}
        </Typography>
      </Box>
    );
  }

  if (gate.eligible) {
    if (location.pathname !== '/customer-home') {
      return <Navigate to="/customer-home" replace />;
    }
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#fafafa' }}>
        <CustomerAppBar restrictNavToHome />
        <Box sx={{ pt: '64px' }}>{children}</Box>
      </Box>
    );
  }

  return <CustomerPendingApprovalScreen />;
}

CustomerLayout.propTypes = {
  children: PropTypes.node.isRequired,
};
